import { type ReactNode, Children, isValidElement } from 'react';

import type { ReferenceLineData } from '../types/ReferenceLineData';

import type { DataType } from './formatters';
import { isElement } from './isElement';

/**
 * Configuration extracted from YAxis and series children.
 * Used by chart components to calculate Y domain synchronously.
 */
export interface YAxisConfig {
  /** Array of data keys from all series components */
  seriesDataKeys: string[];
  /** Whether a YAxis component is present in the chart children */
  hasYAxis: boolean;
  /** Number of ticks for Y-axis (from YAxis numTicks prop, defaults to 5) */
  yAxisNumTicks: number;
  /** All Y values that must be in the domain: ReferenceLine points + ReferenceArea bounds */
  domainInclusionPoints: number[];
  /** ReferenceLine points only — used for boundary padding so lines aren't clipped at edges */
  referenceLinePoints: number[];
  /** Fallback minimum for Y domain when data has no range */
  yAxisFallbackMin?: number;
  /** Fallback maximum for Y domain when data has no range */
  yAxisFallbackMax?: number;
  /** Data type for Y-axis formatting (from YAxis dataType prop) */
  yAxisDataType?: DataType;
  /** Custom tick formatter function (from YAxis tickFormat prop) */
  yAxisTickFormat?: (value: number) => string;
  /** When true, Y domain should use stacked max (0 to sum of series per x). Used for AreaStackSeries. */
  useStackedYDomain?: boolean;
  /** Soft minimum — domain expands to include this value. From YAxis domainMin prop. */
  domainMin?: number;
  /** Soft maximum — domain expands to include this value. From YAxis domainMax prop. */
  domainMax?: number;
}

const isYAxisElement = (child: React.ReactElement) => isElement(child, 'YAxis');
const isSeriesElement = (child: React.ReactElement) => isElement(child, 'Series');
const isAreaSeriesElement = (child: React.ReactElement) => isElement(child, 'AreaSeries');
const isAreaStackSeriesElement = (child: React.ReactElement) => isElement(child, 'AreaStackSeries');
const isBarSeriesElement = (child: React.ReactElement) => isElement(child, 'BarSeries');
const isReferenceLineElement = (child: React.ReactElement) => isElement(child, 'ReferenceLine');
const isReferenceAreaElement = (child: React.ReactElement) => isElement(child, 'ReferenceArea');

interface YAxisElementConfig {
  hasYAxis: boolean;
  numTicks: number;
  fallbackMin?: number;
  fallbackMax?: number;
  dataType?: DataType;
  tickFormat?: (value: number) => string;
  domainMin?: number;
  domainMax?: number;
}

/**
 * Recursively searches children to find YAxis props.
 * Handles fragments and nested children.
 *
 * @param defaultNumTicks - Fallback tick count used when a YAxis element is present but has
 *   no explicit numTicks prop. Typically derived from the chart container height.
 *   When no YAxis is present this parameter is ignored — the hardcoded default of 5 is kept
 *   so the Y scale domain calculation is not affected by the container height.
 */
function findYAxisConfig(children: ReactNode, defaultNumTicks = 5): YAxisElementConfig {
  let hasYAxis = false;
  let numTicks = 5; // Hardcoded default; overridden below only when a YAxis element is found
  let fallbackMin: number | undefined;
  let fallbackMax: number | undefined;
  let dataType: DataType | undefined;
  let tickFormat: ((value: number) => string) | undefined;
  let domainMin: number | undefined;
  let domainMax: number | undefined;

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isYAxisElement(child)) {
      hasYAxis = true;
      const props = child.props as {
        numTicks?: number;
        fallbackMin?: number;
        fallbackMax?: number;
        dataType?: DataType;
        tickFormat?: (value: number) => string;
        domainMin?: number;
        domainMax?: number;
      };
      // Explicit numTicks on <YAxis> always wins. Fall back to the height-derived estimate,
      // then to 5 if no estimate is available. This branch is only entered when a YAxis element
      // exists, keeping scale-domain calculations stable for charts that omit YAxis.
      numTicks = props.numTicks ?? defaultNumTicks;
      if (props.fallbackMin !== undefined) {
        fallbackMin = props.fallbackMin;
      }
      if (props.fallbackMax !== undefined) {
        fallbackMax = props.fallbackMax;
      }
      if (props.dataType !== undefined) {
        dataType = props.dataType;
      }
      tickFormat = props.tickFormat;
      domainMin = props.domainMin;
      domainMax = props.domainMax;
      return;
    }

    // Recurse into fragments and other containers
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);
  return { hasYAxis, numTicks, fallbackMin, fallbackMax, dataType, tickFormat, domainMin, domainMax };
}

/**
 * Recursively extracts all dataKeys from Series, AreaSeries, and BarSeries components.
 * Handles fragments and nested children.
 */
function findSeriesDataKeys(children: ReactNode): string[] {
  const dataKeys: string[] = [];

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    // Check if this is a Series component
    if (isSeriesElement(child)) {
      const props = child.props as { dataKey?: string };
      if (props.dataKey !== undefined) {
        dataKeys.push(props.dataKey);
      }
      return;
    }

    // Check if this is an AreaSeries component
    if (isAreaSeriesElement(child)) {
      const props = child.props as {
        lowerBoundDataKey?: string;
        upperBoundDataKey?: string;
        excludeFromYScale?: boolean;
      };
      // Include bounds in Y scale unless explicitly excluded
      if (!props.excludeFromYScale) {
        if (props.lowerBoundDataKey) {
          dataKeys.push(props.lowerBoundDataKey);
        }
        if (props.upperBoundDataKey) {
          dataKeys.push(props.upperBoundDataKey);
        }
      }
      return;
    }

    // Check if this is an AreaStackSeries component (series array with dataKey per item)
    if (isAreaStackSeriesElement(child)) {
      const props = child.props as { series?: Array<{ dataKey?: string }> };
      if (Array.isArray(props.series)) {
        for (const item of props.series) {
          if (item.dataKey !== undefined) {
            dataKeys.push(item.dataKey);
          }
        }
      }
      return;
    }

    // Check if this is a BarSeries component
    if (isBarSeriesElement(child)) {
      const props = child.props as { dataKey?: string };
      if (props.dataKey !== undefined) {
        dataKeys.push(props.dataKey);
      }
      return;
    }

    // Recurse into fragments and other containers
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  // Return unique dataKeys (in case multiple series share the same dataKey)
  return Array.from(new Set(dataKeys));
}

/**
 * Recursively extracts horizontal reference line points from ReferenceLine components in children.
 * Handles fragments and nested children.
 * For ReferenceLines with direct point prop, extracts the point value.
 * For ReferenceLines with referenceKey, looks up the point in referenceLineData.
 */
function findHorizontalReferenceLinePointsFromChildren(
  children: ReactNode,
  referenceLineData?: ReferenceLineData[],
): number[] {
  const points: number[] = [];

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    // Check if this is a ReferenceLine component
    if (isReferenceLineElement(child)) {
      const props = child.props as {
        type?: 'vertical' | 'horizontal';
        point?: number | string;
        referenceKey?: string;
      };

      // Resolve type: use referenceLineData type if referenceKey is provided, otherwise use prop
      // This matches the logic in ReferenceLine.tsx: const type = refLineData?.type ?? typeProp;
      let resolvedType: 'vertical' | 'horizontal' | undefined = props.type;
      if (props.referenceKey && referenceLineData) {
        const refLineData = referenceLineData.find((d) => d.key === props.referenceKey);
        resolvedType = refLineData?.type ?? props.type;
      }

      // Only process horizontal reference lines
      if (resolvedType === 'horizontal') {
        // Match the priority order from ReferenceLine.tsx: refLineData?.point ?? pointProp
        // If referenceKey is provided, look it up in referenceLineData first (data takes precedence)
        if (props.referenceKey && referenceLineData) {
          const refLineData = referenceLineData.find((d) => d.key === props.referenceKey);
          if (typeof refLineData?.point === 'number') {
            points.push(refLineData.point);
          } else if (typeof props.point === 'number') {
            // Fall back to prop if data doesn't have a point
            points.push(props.point);
          }
        } else if (typeof props.point === 'number') {
          // If no referenceKey, use point prop directly
          points.push(props.point);
        }
      }
      return;
    }

    // Recurse into fragments and other containers
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  return points;
}

/**
 * Recursively extracts Y-axis bounds from ReferenceArea components in children.
 * Returns all finite y1/y2 values so the chart domain includes the full area extent.
 */
function findReferenceAreaBounds(children: ReactNode): number[] {
  const bounds: number[] = [];

  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }

    if (isReferenceAreaElement(child)) {
      const props = child.props as { y1?: number; y2?: number };
      if (props.y1 !== undefined) {
        bounds.push(props.y1);
      }
      if (props.y2 !== undefined) {
        bounds.push(props.y2);
      }
      return;
    }

    // Recurse into fragments and other containers
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };

  Children.forEach(children, searchChild);

  return bounds;
}

/**
 * Returns true if any child is AreaStackSeries (stacked area chart).
 * Used to compute Y domain as stacked max instead of per-series min/max.
 */
function findUseStackedYDomain(children: ReactNode): boolean {
  let found = false;
  const searchChild = (child: ReactNode): void => {
    if (!isValidElement(child)) {
      return;
    }
    if (isAreaStackSeriesElement(child)) {
      found = true;
      return;
    }
    const childProps = child.props as { children?: ReactNode };
    if (childProps.children) {
      Children.forEach(childProps.children, searchChild);
    }
  };
  Children.forEach(children, searchChild);
  return found;
}

/**
 * Extracts Y-axis configuration from chart children and reference line data.
 *
 * Walks the React children tree to find YAxis props, Series data keys,
 * ReferenceLine points, and ReferenceArea bounds. This enables unidirectional
 * data flow: the chart extracts config synchronously at initialization,
 * preventing the Y-domain flash from an initial render with the wrong scale.
 *
 * @param children - React children of the chart component
 * @param referenceLineData - Optional array of reference line data from chart props
 *
 * @example
 * ```tsx
 * function LineChart({ children, referenceLineData, ... }) {
 *   const yAxisConfig = useMemo(
 *     () => extractYAxisConfig(children, referenceLineData),
 *     [children, referenceLineData]
 *   );
 *   const chartProviderValue = useChartProviderState({ yAxisConfig, ... });
 * }
 * ```
 */
export function extractYAxisConfig(
  children: ReactNode,
  referenceLineData?: ReferenceLineData[],
  defaultNumTicks?: number,
): YAxisConfig {
  const seriesDataKeys = findSeriesDataKeys(children);
  const {
    hasYAxis,
    numTicks: yAxisNumTicks,
    fallbackMin: yAxisFallbackMin,
    fallbackMax: yAxisFallbackMax,
    dataType: yAxisDataType,
    tickFormat: yAxisTickFormat,
    domainMin,
    domainMax,
  } = findYAxisConfig(children, defaultNumTicks);

  const refLinePoints = findHorizontalReferenceLinePointsFromChildren(children, referenceLineData);
  const refAreaBounds = findReferenceAreaBounds(children);

  const useStackedYDomain = findUseStackedYDomain(children);

  return {
    seriesDataKeys,
    hasYAxis,
    yAxisNumTicks,
    domainInclusionPoints: [...refLinePoints, ...refAreaBounds],
    referenceLinePoints: refLinePoints,
    yAxisFallbackMin,
    yAxisFallbackMax,
    yAxisDataType,
    yAxisTickFormat,
    useStackedYDomain,
    domainMin,
    domainMax,
  };
}
